import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs, collectMulti } from './context/args'
import { emitJSON } from './context/render'
import { resolveWorkspaceSlug } from './context'
import {
  currentLocalWorkIntent,
  detectRepoContext,
  loadLocalWorkIntent,
  saveLocalWorkIntent,
  clearLocalWorkIntent,
  type LocalWorkIntent,
} from '../work-state'
import { WORK_INTENT_OUTCOMES, WORK_INTENT_CARD_STATUSES } from '@driftless/topic-schema'

// Active Work CLI — ephemeral execution sessions over Project Cards. The flow:
// `project card next` picks the card → `work start` runs the deterministic
// preflight and registers the live session → hooks stream observed files /
// heartbeats → `work finish|abandon` closes it (the Card moves only where
// explicitly asked). Repo + branch auto-detect from git; the active intent for
// this repo/branch is remembered locally so finish/heartbeat need no id.

// Single-sourced help (help-consistency pattern): the text lives in
// help-text.ts; index.ts and this module both reference the same const.
export { WORK_HELP } from './help-text'
import { WORK_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(WORK_HELP)
}

// Resolve the intent id for id-optional subcommands: explicit positional wins;
// otherwise the locally-saved session for the current repo/branch.
function resolveIntentId(positional: string[], required = true): { id: string | null; local: LocalWorkIntent | null } {
  const explicit = positional[1]?.trim()
  const local = currentLocalWorkIntent()
  if (explicit) return { id: explicit, local }
  if (local) return { id: local.intent_id, local }
  if (required) {
    console.error('Error: no intent id given and no active local session for this repo/branch.')
    console.error('Pass the id explicitly: driftless work <subcommand> <intent-id> …')
    process.exit(1)
  }
  return { id: null, local: null }
}

function repoContextOrFlags(flags: Record<string, string | boolean>): { repository_key: string; branch: string } {
  const detected = detectRepoContext()
  const repository_key = (typeof flags['repo'] === 'string' && flags['repo'].trim()) || detected.repository_key || ''
  const branch = (typeof flags['branch'] === 'string' && flags['branch'].trim()) || detected.branch || ''
  if (!repository_key || !branch) {
    console.error('Error: could not detect repository/branch from git.')
    console.error('Run inside a git checkout with an origin remote, or pass --repo org/repo --branch <name>.')
    process.exit(1)
  }
  return { repository_key: repository_key.toLowerCase(), branch }
}

function severityIcon(s: string): string {
  return s === 'high' ? '!' : s === 'medium' ? '~' : '·'
}

function printPreflight(preflight: { verdict: string; overlaps: Array<{ kind: string; severity: string; objective: string; evidence: string[] }>; summary?: string }): void {
  console.log(`Preflight: ${preflight.verdict.toUpperCase()}`)
  if (preflight.summary) console.log(`\n${preflight.summary}`)
  for (const o of preflight.overlaps ?? []) {
    console.log(`  ${severityIcon(o.severity)} [${o.kind}/${o.severity}] "${o.objective}"`)
    for (const e of o.evidence ?? []) console.log(`    - ${e}`)
  }
}

// Best-effort title lookups so the human output names things, not uuids. Any
// failure falls back to the id — never blocks the command.
async function titleOf(path: string, fallback: string): Promise<string> {
  try {
    const res = (await api.get(path, { timeoutMs: 4000 })) as { title?: string }
    return res?.title || fallback
  } catch {
    return fallback
  }
}

export async function workCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help' || sub === undefined) {
    printHelp(sub === undefined ? process.stderr : process.stdout)
    process.exit(sub === undefined ? 1 : 0)
  }

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}/work-intents`

  try {
    switch (sub) {
      case 'start': {
        const project = flags['project']
        const card = flags['card']
        const objective = flags['objective']
        if (typeof project !== 'string' || typeof card !== 'string' || typeof objective !== 'string' || !objective.trim()) {
          console.error('Error: --project <id>, --card <id> and --objective "..." are required.')
          console.error('Usage: driftless work start --project <id> --card <id> --objective "..." [--plan "step"]… [--file path]…')
          process.exit(1)
        }
        const { repository_key, branch } = repoContextOrFlags(flags)
        const body: Record<string, unknown> = {
          project_id: project,
          card_id: card,
          repository_key,
          branch,
          objective: objective.trim(),
          source: 'cli',
        }
        const plan = collectMulti(args, ['--plan'])
        const files = collectMulti(args, ['--file'])
        const topics = collectMulti(args, ['--topic'])
        const areas = collectMulti(args, ['--area'])
        if (plan.length) body['plan'] = plan
        if (files.length) body['declared_files'] = files
        if (topics.length) body['topic_refs'] = topics
        if (areas.length) body['area_refs'] = areas
        if (typeof flags['agent'] === 'string') body['agent_name'] = flags['agent']

        const result = (await api.post(`${base}/start`, body)) as any
        analyticsEvent('work_start', ws, { verdict: result?.preflight?.verdict, files: files.length })

        if (result?.preflight?.verdict === 'conflict') {
          if (isJSON) { emitJSON(result); break }
          console.log('WORK NOT STARTED\n')
          printPreflight(result.preflight)
          console.log('\nAnother session already runs this card — coordinate or pick another card.')
          await flushAndExit(1)
          return
        }

        // Remember MY session for this repo/branch so finish/heartbeat/hooks
        // need no id. Local memory only — the cloud row is the truth.
        saveLocalWorkIntent({
          intent_id: result.intent.id,
          workspace: ws,
          repository_key,
          branch,
          project_id: project,
          card_id: card,
          objective: objective.trim(),
          started_at: result.intent.created_at,
          reported_files: [],
        })

        if (isJSON) { emitJSON(result); break }
        const [projectTitle, cardTitle] = await Promise.all([
          titleOf(`/workspaces/${ws}/projects/${encodeURIComponent(project)}`, project),
          titleOf(`/workspaces/${ws}/projects/${encodeURIComponent(project)}/cards/${encodeURIComponent(card)}`, card),
        ])
        console.log('WORK STARTED\n')
        console.log(`Project: ${projectTitle}`)
        console.log(`Card: ${cardTitle}`)
        console.log(`Branch: ${branch}`)
        printPreflight(result.preflight)
        if (result.card_transition) console.log(`\nCard: ${result.card_transition.from} → ${result.card_transition.to}`)
        console.log(`\nIntent ${result.intent.id} — expires ${result.intent.expires_at} (heartbeats renew it).`)
        console.log('Close with: driftless work finish --outcome success   (or work abandon)')
        break
      }

      case 'status': {
        const local = currentLocalWorkIntent()
        if (!local) {
          if (isJSON) { emitJSON({ active: false }); break }
          console.log('No active local work session for this repo/branch.')
          console.log('Start one with: driftless work start --project <id> --card <id> --objective "..."')
          break
        }
        const intent = (await api.get(`${base}/${encodeURIComponent(local.intent_id)}`)) as any
        if (isJSON) { emitJSON(intent); break }
        console.log(`WORK ${String(intent.lifecycle).toUpperCase()}\n`)
        console.log(`Objective: ${intent.objective}`)
        console.log(`Repo: ${intent.repository_key}  Branch: ${intent.branch}`)
        console.log(`Card: ${intent.card_id}  (project ${intent.project_id})`)
        console.log(`Heartbeat: ${intent.heartbeat_at}  Expires: ${intent.expires_at}`)
        if (intent.observed_files?.length) console.log(`Observed files: ${intent.observed_files.length}`)
        if (intent.lifecycle !== 'active') {
          clearLocalWorkIntent(local.repository_key, local.branch)
          console.log('\n(local session cleared — the intent is no longer active)')
        }
        break
      }

      case 'active': {
        const params = new URLSearchParams()
        const detected = detectRepoContext()
        const repo = (typeof flags['repo'] === 'string' && flags['repo'].trim()) || detected.repository_key
        if (typeof flags['project'] === 'string') params.set('project_id', flags['project'])
        if (typeof flags['card'] === 'string') params.set('card_id', flags['card'])
        if (repo && flags['all'] !== true) params.set('repository_key', repo)
        if (typeof flags['branch'] === 'string' && flags['branch'].trim()) params.set('branch', flags['branch'].trim())
        const qs = params.toString()
        const result = (await api.get(`${base}/active${qs ? `?${qs}` : ''}`)) as any
        if (isJSON) { emitJSON(result); break }
        const rows = result?.intents ?? []
        if (!rows.length) { console.log('No active work sessions.'); break }
        console.log(`${rows.length} active session(s)${repo && flags['all'] !== true ? ` in ${repo}` : ''}:\n`)
        for (const w of rows) {
          console.log(`${w.id}  [${w.branch}]  ${w.agent_name ?? w.actor_id ?? w.source}`)
          console.log(`  ${w.objective}`)
          if (w.declared_files?.length) console.log(`  files: ${w.declared_files.slice(0, 5).join(', ')}${w.declared_files.length > 5 ? ', …' : ''}`)
          console.log(`  card ${w.card_id} · beat ${w.heartbeat_at}`)
        }
        break
      }

      case 'update': {
        const { id } = resolveIntentId(positional)
        const body: Record<string, unknown> = {}
        if (typeof flags['objective'] === 'string') body['objective'] = flags['objective']
        if (typeof flags['agent'] === 'string') body['agent_name'] = flags['agent']
        const plan = collectMulti(args, ['--plan'])
        const files = collectMulti(args, ['--file'])
        const observed = collectMulti(args, ['--observed'])
        const topics = collectMulti(args, ['--topic'])
        const areas = collectMulti(args, ['--area'])
        if (plan.length) body['plan'] = plan
        if (files.length) body['declared_files'] = files
        if (observed.length) body['observed_files'] = observed
        if (topics.length) body['topic_refs'] = topics
        if (areas.length) body['area_refs'] = areas
        if (Object.keys(body).length === 0) {
          console.error('Error: nothing to update — pass --objective / --plan / --file / --observed / --topic / --area / --agent.')
          process.exit(1)
        }
        const result = (await api.patch(`${base}/${encodeURIComponent(id!)}`, body)) as any
        analyticsEvent('work_update', ws, { fields: Object.keys(body).length })
        if (isJSON) emitJSON(result)
        else console.log(`Work intent updated — TTL renewed to ${result.expires_at}`)
        break
      }

      case 'heartbeat': {
        const { id } = resolveIntentId(positional)
        const result = (await api.post(`${base}/${encodeURIComponent(id!)}/heartbeat`, {})) as any
        if (isJSON) emitJSON(result)
        else console.log(`Heartbeat ok — expires ${result.expires_at}`)
        break
      }

      case 'finish': {
        const { id, local } = resolveIntentId(positional)
        const body: Record<string, unknown> = {}
        if (typeof flags['outcome'] === 'string') {
          if (!(WORK_INTENT_OUTCOMES as readonly string[]).includes(flags['outcome'])) {
            console.error(`Error: --outcome must be one of: ${WORK_INTENT_OUTCOMES.join(', ')}`); process.exit(1)
          }
          body['outcome'] = flags['outcome']
        }
        if (typeof flags['summary'] === 'string') body['summary'] = flags['summary']
        if (typeof flags['card-status'] === 'string') {
          if (!(WORK_INTENT_CARD_STATUSES as readonly string[]).includes(flags['card-status'])) {
            console.error(`Error: --card-status must be one of: ${WORK_INTENT_CARD_STATUSES.join(', ')}`); process.exit(1)
          }
          body['card_status'] = flags['card-status']
        }
        if (flags['validation-passed'] !== undefined) body['validation_passed'] = flags['validation-passed'] === true || flags['validation-passed'] === 'true'
        if (typeof flags['validation-result'] === 'string') body['validation_result'] = flags['validation-result']
        const result = (await api.post(`${base}/${encodeURIComponent(id!)}/finish`, body)) as any
        analyticsEvent('work_finish', ws, { outcome: body['outcome'] ?? 'success', card_status: body['card_status'] ?? null })
        if (local) clearLocalWorkIntent(local.repository_key, local.branch)
        if (isJSON) { emitJSON(result); break }
        console.log(`WORK FINISHED — outcome ${result.intent.outcome}`)
        if (result.card_transition) console.log(`Card: ${result.card_transition.from} → ${result.card_transition.to}`)
        else console.log('Card: unchanged')
        break
      }

      case 'abandon': {
        const { id, local } = resolveIntentId(positional)
        const body: Record<string, unknown> = {}
        if (typeof flags['reason'] === 'string') body['reason'] = flags['reason']
        if (typeof flags['card-status'] === 'string') {
          if (!(WORK_INTENT_CARD_STATUSES as readonly string[]).includes(flags['card-status'])) {
            console.error(`Error: --card-status must be one of: ${WORK_INTENT_CARD_STATUSES.join(', ')}`); process.exit(1)
          }
          body['card_status'] = flags['card-status']
        }
        const result = (await api.post(`${base}/${encodeURIComponent(id!)}/abandon`, body)) as any
        analyticsEvent('work_abandon', ws, { card_status: body['card_status'] ?? null })
        if (local) clearLocalWorkIntent(local.repository_key, local.branch)
        if (isJSON) { emitJSON(result); break }
        console.log('WORK ABANDONED')
        if (result.card_transition) console.log(`Card: ${result.card_transition.from} → ${result.card_transition.to}`)
        else console.log('Card: unchanged')
        break
      }

      default:
        console.error(`Unknown work subcommand: '${sub}'. Run 'driftless work --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}
